import * as THREE from 'three';

import { type LabSceneFactory, type LabStage, createLabStage, createRippleSurface, labGlassMaterial, refractiveLiquidMaterial } from './labScene3d';

/** What the 3D coffee-cup calorimeter shows. Every value comes from the Explore setup. */
export interface CalorimeterState {
  volumeMl: number;
  startTemperatureC: number;
  /** The thermometer column follows this reading. */
  temperatureC: number;
}

/** Named states for screenshot review of every visible change. */
export const ENERGY_CALORIMETRY_PREVIEW_STATES: Record<string, CalorimeterState> = {
  start: { volumeMl: 100, startTemperatureC: 21, temperatureC: 21 },
  exothermic: { volumeMl: 100, startTemperatureC: 21, temperatureC: 27.6 },
  'strongly-exothermic': { volumeMl: 50, startTemperatureC: 21, temperatureC: 45.5 },
  endothermic: { volumeMl: 50, startTemperatureC: 21, temperatureC: 14 },
  'full-cup': { volumeMl: 500, startTemperatureC: 21, temperatureC: 30 },
  'small-hot': { volumeMl: 10, startTemperatureC: 60, temperatureC: 90 },
};

// World units; a teaching view of one lidded cup, not to scale.
const CAPACITY_ML = 500;
const CUP_H = 2.6;
const CUP_R_BOTTOM = 1.0;
const CUP_R_TOP = 1.35;
const WALL = 0.12;
const INNER_CUP_SCALE = 0.9;
const INNER_CUP_LIFT = 0.3;
/** Front wedge cut away so the solution inside stays visible (opaque polystyrene). */
const CUT_START = Math.PI / 4;
const CUT_LENGTH = Math.PI * 1.5;
const MAX_LIQUID_H = 2.05;
const MIN_LIQUID_H = 0.08;
const T_MIN = 0;
const T_MAX = 100;
/** ΔT that gives the strongest warm or cool cue. */
const CUE_FULL_DELTA_C = 25;
const THERMO = { x: 0.42, z: -0.3, tilt: -0.12, length: 3.7 } as const;

/** Closed wall profile (outer face up, rim, inner face down) of one polystyrene cup. */
function cupProfile(): THREE.Vector2[] {
  return [
    [0, 0], [CUP_R_BOTTOM, 0], [CUP_R_TOP, CUP_H], [CUP_R_TOP + 0.04, CUP_H + 0.04],
    [CUP_R_TOP - WALL, CUP_H + 0.04], [CUP_R_TOP - WALL, CUP_H], [CUP_R_BOTTOM - WALL * 0.8, WALL], [0, WALL],
  ].map(([x, y]) => new THREE.Vector2(x, y));
}

/** Inner-face radius of an unscaled cup at local height y. */
function innerRadiusAt(y: number): number {
  const t = THREE.MathUtils.clamp((y - WALL) / (CUP_H - WALL), 0, 1);
  return CUP_R_BOTTOM - WALL * 0.8 + t * (CUP_R_TOP - WALL - (CUP_R_BOTTOM - WALL * 0.8));
}

/** A cut-away cup: lathe wall plus flat faces where the wedge is removed, so the wall reads as solid. */
function createCup(stage: LabStage, material: THREE.Material): THREE.Group {
  const group = new THREE.Group();
  const profile = cupProfile();
  const wall = new THREE.Mesh(stage.keep(new THREE.LatheGeometry(profile, 96, CUT_START, CUT_LENGTH)), material);
  wall.castShadow = true;
  wall.receiveShadow = true;
  group.add(wall);
  const faceGeometry = stage.keep(new THREE.ShapeGeometry(new THREE.Shape(profile)));
  [CUT_START, CUT_START + CUT_LENGTH].forEach((phi) => {
    const face = new THREE.Mesh(faceGeometry, material);
    // Lathe vertices sit at (r sin φ, y, r cos φ); rotating the XY profile by φ − π/2 matches that plane.
    face.rotation.y = phi - Math.PI / 2;
    group.add(face);
  });
  return group;
}

export const createCalorimeterScene: LabSceneFactory<CalorimeterState> = (renderer, palette, reducedMotion) => {
  const stage = createLabStage(renderer, palette, reducedMotion, { cameraPosition: [0.4, 2.7, 11.6], target: [0, 0.25, 0] });
  const { scene, keep, benchY } = stage;

  // Two nested polystyrene cups: opaque, matte white; the outer cup has a faint cool tint so the two read apart.
  const foam = (tint: number) =>
    keep(new THREE.MeshStandardMaterial({ color: palette.bench.clone().lerp(palette.ice, tint), roughness: 0.92, side: THREE.DoubleSide }));
  const outerCup = createCup(stage, foam(0.45));
  outerCup.position.y = benchY;
  const innerCup = createCup(stage, foam(0.08));
  innerCup.scale.set(INNER_CUP_SCALE, 1, INNER_CUP_SCALE);
  innerCup.position.y = benchY + INNER_CUP_LIFT;
  scene.add(outerCup, innerCup);
  // Dark rubber lab mat under the cups so the white foam reads against the white bench.
  const mat = new THREE.Mesh(
    keep(new THREE.CylinderGeometry(2.3, 2.3, 0.05, 64)),
    keep(new THREE.MeshStandardMaterial({ color: palette.metal.clone().lerp(palette.bench, 0.25), roughness: 0.85 })),
  );
  mat.position.y = benchY + 0.005;
  mat.receiveShadow = true;
  scene.add(mat);
  const innerFloorY = benchY + INNER_CUP_LIFT + WALL;
  const lidY = benchY + INNER_CUP_LIFT + CUP_H + 0.07;

  // Lid: a thin cut-away disc on the inner cup rim.
  const lid = new THREE.Mesh(
    keep(new THREE.CylinderGeometry(CUP_R_TOP * INNER_CUP_SCALE + 0.06, CUP_R_TOP * INNER_CUP_SCALE + 0.06, 0.08, 96, 1, false, CUT_START, CUT_LENGTH)),
    keep(new THREE.MeshStandardMaterial({ color: palette.bench.clone().lerp(palette.metal, 0.08), roughness: 0.85, side: THREE.DoubleSide })),
  );
  lid.position.y = lidY;
  lid.castShadow = true;
  scene.add(lid);

  // Solution: refractive, tapered with the cup; rebuilt only when the level changes.
  const liquidMaterial = refractiveLiquidMaterial(stage, palette.liquid, { tint: 0.45, attenuationDistance: 3.4 });
  const liquid = new THREE.Mesh(new THREE.CylinderGeometry(1, 1, 1, 64), liquidMaterial);
  scene.add(liquid);
  const surfaceBaseR = innerRadiusAt(WALL + MAX_LIQUID_H) * INNER_CUP_SCALE;
  const surface = createRippleSurface(stage, surfaceBaseR, liquidMaterial, 14, 64);
  let liquidHeight = -1;
  const setLevel = (height: number) => {
    if (Math.abs(height - liquidHeight) < 1e-4) return;
    liquidHeight = height;
    const bottomR = innerRadiusAt(WALL) * INNER_CUP_SCALE - 0.015;
    const topR = innerRadiusAt(WALL + height) * INNER_CUP_SCALE - 0.015;
    liquid.geometry.dispose();
    liquid.geometry = new THREE.CylinderGeometry(topR, bottomR, height, 64);
    liquid.position.y = innerFloorY + height / 2;
    surface.mesh.position.y = innerFloorY + height;
    surface.mesh.scale.set(topR / surfaceBaseR, 1, topR / surfaceBaseR);
  };

  // Warm or cool cue: a soft light inside the cup, coral when the solution warmed, pale blue when it cooled.
  const cueLight = new THREE.PointLight(palette.heat, 0, 4, 1.6);
  cueLight.position.set(0, innerFloorY + 0.6, 0.2);
  scene.add(cueLight);
  const baseAttenuation = palette.liquid.clone();

  // Thermometer through the lid: glass tube, red bulb in the solution, red column that follows the reading.
  const thermo = new THREE.Group();
  thermo.position.set(THERMO.x, innerFloorY + 0.12, THERMO.z);
  thermo.rotation.z = THERMO.tilt;
  const tubeMaterial = labGlassMaterial(stage, 0.35);
  tubeMaterial.side = THREE.DoubleSide;
  const tube = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.09, 0.09, THERMO.length, 24)), tubeMaterial);
  tube.position.y = THERMO.length / 2;
  tube.renderOrder = 12;
  const redMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.accent, roughness: 0.2, clearcoat: 1, emissive: palette.accent, emissiveIntensity: 0.35 }));
  const bulb = new THREE.Mesh(keep(new THREE.SphereGeometry(0.14, 20, 14)), redMaterial);
  const column = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.045, 0.045, 1, 12)), redMaterial);
  thermo.add(tube, bulb, column);
  scene.add(thermo);

  // Stirrer: a thin metal rod through the lid with a ring near the floor.
  const metal = keep(new THREE.MeshStandardMaterial({ color: palette.metal.clone().lerp(palette.bench, 0.55), metalness: 0.8, roughness: 0.3 }));
  const rod = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.03, 0.03, 3.2, 12)), metal);
  rod.position.set(-0.5, innerFloorY + 1.7, -0.25);
  const ring = new THREE.Mesh(keep(new THREE.TorusGeometry(0.28, 0.025, 8, 32)), metal);
  ring.rotation.x = Math.PI / 2;
  ring.position.set(-0.5, innerFloorY + 0.15, -0.25);
  scene.add(rod, ring);

  stage.finish();

  return {
    update: (state, t, playing) => {
      const fill = THREE.MathUtils.clamp(state.volumeMl / CAPACITY_ML, 0, 1);
      setLevel(Math.max(MIN_LIQUID_H, fill * MAX_LIQUID_H));
      surface.update(playing ? 0.008 : 0, t, false);

      const heat = THREE.MathUtils.clamp((state.temperatureC - T_MIN) / (T_MAX - T_MIN), 0, 1);
      const columnLength = 0.12 + heat * (THERMO.length - 0.5);
      column.scale.y = columnLength;
      column.position.y = columnLength / 2;

      const change = state.temperatureC - state.startTemperatureC;
      const strength = THREE.MathUtils.clamp(Math.abs(change) / CUE_FULL_DELTA_C, 0, 1);
      const cue = change >= 0 ? palette.heat : palette.ice;
      cueLight.color.copy(cue);
      cueLight.intensity = strength * 6;
      liquidMaterial.attenuationColor.copy(baseAttenuation).lerp(cue, strength * 0.35);

      // The front cut-away stays toward the viewer, so the view never auto-rotates.
      stage.render(false);
    },
    resize: stage.resize,
    dispose: () => {
      liquid.geometry.dispose();
      stage.dispose();
    },
  };
};
